import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

interface PlotlyChartProps {
  data: any;
  layout?: any;
  config?: any;
  style?: React.CSSProperties;
}

const PlotlyChart: React.FC<PlotlyChartProps> = ({ 
  data, 
  layout = {}, 
  config = { displayModeBar: false },
  style = { width: "100%", height: "440px" }
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartRef.current && data) {
      const loadChart = async () => {
        try {
          // Try to load Plotly from CDN as the most reliable method
          if (!(window as any).Plotly) {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdn.plot.ly/plotly-latest.min.js';
              script.onload = resolve;
              script.onerror = reject;
              document.head.appendChild(script);
            });
          }
          
          const Plotly = (window as any).Plotly;
          
          if (!Plotly) {
            throw new Error("Failed to load Plotly from CDN");
          }
          
          // Clear previous content
          if (chartRef.current) {
            chartRef.current.innerHTML = "";
            
            // Create the plot
            Plotly.newPlot(chartRef.current, data, {
              ...layout,
              autosize: true,
              height:440,
              margin: { l: 50, r: 50, t: 50, b: 100 }, // more bottom space for rotated labels
              xaxis: {
                tickangle: -45, // rotate labels
                automargin: true
              }
            }, config);
          }
          
          setError(null);
        } catch (error) {
          console.error("Failed to load chart:", error);
          setError("Chart could not be loaded");
          
          // Fallback: show data as JSON
          if (chartRef.current) {
            chartRef.current.innerHTML = `
              <div style="padding: 20px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
                <div style="color: #666; margin-bottom: 10px;">Chart could not be loaded</div>
                <div style="color: #333; font-size: 12px; margin-bottom: 10px;">Chart Data:</div>
                <pre style="font-size: 11px; color: #333; overflow: auto; max-height: 300px; background: #f5f5f5; padding: 10px; border-radius: 4px;">${JSON.stringify(data, null, 2)}</pre>
              </div>
            `;
          }
        }
      };
      
      loadChart();
    }
  }, [data, layout, config]);

  if (error) {
    return (
      <Box sx={{ 
        width: "100%", 
        height: "400px", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        border: "1px solid #ddd",
        borderRadius: "4px",
        backgroundColor: "#f9f9f9"
      }}>
        <div style={{ textAlign: "center", color: "#666" }}>
          <div>{error}</div>
          <div style={{ fontSize: "12px", marginTop: "8px" }}>Check console for details</div>
        </div>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", my: 2, 
      "& svg": { height: "100% !important" }
     }}>
      <div ref={chartRef} style={style} />
    </Box>
  );
};

export default PlotlyChart; 